import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type InternalNoteWireItem } from '../lib/api-client';

/**
 * useInternalNotes(conversationId) — Fatia G (ADR-049 + Caderno cap 39).
 *
 * Lista notas internas da conversa (filtradas server-side por visibilidade
 * do requester) + mutations create/update/delete.
 */
export function useInternalNotes(conversationId: string | undefined): {
  data: InternalNoteWireItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['internal-notes', conversationId],
    queryFn: async () => {
      if (conversationId === undefined) throw new Error('conversationId required');
      const res = await api.listInternalNotes(conversationId);
      return res.data;
    },
    enabled: conversationId !== undefined,
    staleTime: 30 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useCreateInternalNote(
  conversationId: string,
): ReturnType<
  typeof useMutation<{ noteId: string }, Error, Parameters<typeof api.createInternalNote>[1]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createInternalNote>[1]) =>
      api.createInternalNote(conversationId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['internal-notes', conversationId] });
    },
  });
}

export function useUpdateInternalNote(
  conversationId: string,
): ReturnType<
  typeof useMutation<
    { version: number },
    Error,
    {
      noteId: string;
      version: number;
      content?: string;
      visibility?: 'public_team' | 'private_author' | 'private_supervisors';
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      noteId,
      version,
      ...body
    }: {
      noteId: string;
      version: number;
      content?: string;
      visibility?: 'public_team' | 'private_author' | 'private_supervisors';
    }) => api.updateInternalNote(noteId, version, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['internal-notes', conversationId] });
    },
  });
}

export function useDeleteInternalNote(
  conversationId: string,
): ReturnType<typeof useMutation<{ version: number }, Error, { noteId: string; version: number }>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, version }: { noteId: string; version: number }) =>
      api.deleteInternalNote(noteId, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['internal-notes', conversationId] });
    },
  });
}
